import { fechaYMD } from './date';
import React from 'react';
import { Tag } from 'antd';

// Parser para transformar los logs del backend al formato requerido por el frontend
export const parserLogs = (log) => {
  // console.log('Parsing log:', log);
  
  if (!log) return null;
  
  // Desestructuración más flexible
  const {
    _id,
    id,
    usuario,
    modulo,
    accion,
    recursoId,
    recursoNombre,
    descripcion,
    datosAnteriores,
    datosNuevos,
    ip,
    userAgent,
    createdAt,
    updatedAt,
    timestamp,
    fecha
  } = log;

  const logId = _id || id;
  const logFecha = createdAt || timestamp || fecha;
  const logUsuario = usuario?.nombre || usuario?.email || usuario || 'Usuario desconocido';

  // Función helper para formatear fechas de manera segura
  const formatearFecha = (fechaString) => {
    // console.log('Formatting fecha:', fechaString, 'Type:', typeof fechaString);
    
    if (!fechaString) return 'Fecha no disponible';
    
    try {
      const fecha = new Date(fechaString);
      // console.log('New Date result:', fecha, 'isNaN:', isNaN(fecha.getTime()));
      
      if (isNaN(fecha.getTime())) {
        return 'Fecha inválida';
      }
      
      // Usar el formato año-mes-día como pediste
      const result = fechaYMD(fechaString);
      // console.log('fechaYMD result:', result);
      return result;
    } catch (error) {
      console.error('Error al formatear fecha:', error, fechaString);
      return 'Error en fecha';
    }
  };

  const parsedLog = {
    _id: logId,
    key: logId,
    usuario: logUsuario,
    usuarioId: usuario?._id || null,
    modulo: modulo || 'No definido',
    accion: accion || 'No definida',
    recursoId: recursoId || '',
    recursoNombre: recursoNombre || '',
    descripcion: descripcion || '',
    datosAnteriores: datosAnteriores || null,
    datosNuevos: datosNuevos || null,
    ip: ip || '',
    userAgent: userAgent || '',
    fecha: formatearFecha(logFecha),
    timestamp: logFecha,
    fechaCreacion: createdAt,
    fechaActualizacion: updatedAt,
    detalles: JSON.stringify({ datosAnteriores, datosNuevos }, null, 2)
  };

  // console.log('Parsed log:', parsedLog);
  return parsedLog;
};

// Colores para módulos
export const getModuloColor = (modulo) => {
  const colors = {
    sensor: '#52c41a',
    tarjeta: '#1890ff',
    proyecto: '#722ed1',
    elemento: '#fa8c16',
    privilegios: '#eb2f96',
    dispositivo: '#13c2c2',
  };
  return colors[modulo] || '#666';
};

// Colores para acciones
export const getAccionColor = (accion) => {
  const colors = {
    crear: '#52c41a',
    actualizar: '#1890ff',
    eliminar: '#f5222d',
    consultar: '#faad14',
    controlar: '#722ed1',
    configurar: '#fa541c',
  };
  return colors[accion] || '#666';
};

// Columnas para la tabla de logs
export const columnsLogs = [
  {
    title: 'Fecha',
    dataIndex: 'fecha',
    key: 'fecha',
    width: 150,
    sorter: (a, b) => new Date(a.fechaCreacion) - new Date(b.fechaCreacion),
  },
  {
    title: 'Usuario',
    dataIndex: 'usuario',
    key: 'usuario',
    width: 120,
    ellipsis: true,
  },
  {
    title: 'Módulo',
    dataIndex: 'modulo',
    key: 'modulo',
    width: 100,
    render: (modulo) => (
      <Tag color={getModuloColor(modulo)}>
        {modulo}
      </Tag>
    ),
  },
  {
    title: 'Acción',
    dataIndex: 'accion',
    key: 'accion',
    width: 100,
    render: (accion) => (
      <Tag color={getAccionColor(accion)}>
        {accion}
      </Tag>
    ),
  },
  {
    title: 'Recurso',
    dataIndex: 'recursoNombre',
    key: 'recursoNombre',
    width: 150,
    ellipsis: true,
  },
  {
    title: 'Descripción',
    dataIndex: 'descripcion',
    key: 'descripcion',
    ellipsis: true,
  },
];

// Opciones para filtros de módulos
export const moduloOptions = [
  { value: 'sensor', label: 'Sensor' },
  { value: 'tarjeta', label: 'Tarjeta' },
  { value: 'proyecto', label: 'Proyecto' },
  { value: 'elemento', label: 'Elemento' },
  { value: 'privilegios', label: 'Privilegios' },
  { value: 'dispositivo', label: 'Dispositivo' },
];

// Opciones para filtros de acciones
export const accionOptions = [
  { value: 'crear', label: 'Crear' },
  { value: 'actualizar', label: 'Actualizar' },
  { value: 'eliminar', label: 'Eliminar' },
  { value: 'consultar', label: 'Consultar' },
  { value: 'controlar', label: 'Controlar' },
  { value: 'configurar', label: 'Configurar' },
];

// Formatear rango de fechas para los filtros
export const formatDateRange = (fechaInicio, fechaFin) => {
  // Función helper para formatear fecha evitando problemas de zona horaria
  const formatearFechaLocal = (fechaString) => {
    if (!fechaString) return '';
    
    // Si la fecha viene en formato YYYY-MM-DD, crear la fecha sin zona horaria
    const fechaParts = fechaString.split('-');
    if (fechaParts.length === 3) {
      const fecha = new Date(parseInt(fechaParts[0]), parseInt(fechaParts[1]) - 1, parseInt(fechaParts[2]));
      return fecha.toLocaleDateString('es-ES');
    }
    
    // Fallback para otros formatos
    return new Date(fechaString).toLocaleDateString('es-ES');
  };

  if (fechaInicio && fechaFin) {
    const inicio = formatearFechaLocal(fechaInicio);
    const fin = formatearFechaLocal(fechaFin);
    return `${inicio} - ${fin}`;
  }
  if (fechaInicio) {
    return `Desde: ${formatearFechaLocal(fechaInicio)}`;
  }
  if (fechaFin) {
    return `Hasta: ${formatearFechaLocal(fechaFin)}`;
  }
  return '';
};
